import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYSIS_NAME_EXISTS_ERROR } from '../../../../services/api/companyAnalysisService';

interface SaveAnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string) => Promise<boolean>;
    defaultName?: string;
}

export const SaveAnalysisModal = ({ isOpen, onClose, onSave, defaultName = '' }: SaveAnalysisModalProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState(defaultName);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setName(defaultName);
            setError('');
        }
    }, [isOpen, defaultName]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || isLoading) return;

        setIsLoading(true);
        setError('');

        try {
            const success = await onSave(name.trim());
            if (success) {
                setName('');
                onClose();
            } else {
                setError(t('features.swimlane.errorSavingAnalysis') || 'Erreur lors de la sauvegarde');
            }
        } catch (err: any) {
            const message = err?.message === ANALYSIS_NAME_EXISTS_ERROR
                ? (t('features.swimlane.analysisNameAlreadyExists') || 'Une analyse avec ce nom existe déjà dans votre entreprise.')
                : (err?.message || t('features.swimlane.errorSavingAnalysis') || 'Erreur lors de la sauvegarde');
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 9999, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
            <div
                className="analysis-scope-modal rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl"
                style={{ backgroundColor: '#fff', color: '#1f2937' }}
            >
                <h3
                    className="text-lg font-semibold mb-4"
                    style={{ color: '#111827' }}
                >
                    {t('features.swimlane.saveAnalysis')}
                </h3>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label
                            htmlFor="analysis-name"
                            className="block text-sm font-medium mb-2"
                            style={{ color: '#374151' }}
                        >
                            {t('features.swimlane.analysisName')}
                        </label>
                        <input
                            type="text"
                            id="analysis-name"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setError('');
                            }}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            style={{
                                color: '#111827',
                                backgroundColor: '#fff',
                                borderColor: '#d1d5db',
                            }}
                            placeholder={t('features.swimlane.enterAnalysisName')}
                            autoFocus
                            disabled={isLoading}
                            maxLength={100}
                        />
                        {error && (
                            <div className="mt-2 text-sm" style={{ color: '#FF6B6B' }}>
                                <i className="fa-solid fa-exclamation-circle me-1"></i>
                                {error}
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg transition-colors"
                            style={{ color: '#374151', backgroundColor: '#f3f4f6' }}
                            disabled={isLoading}
                        >
                            {t('words.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={!name.trim() || isLoading}
                            className="px-4 py-2 rounded-lg transition-colors"
                            style={
                                name.trim() && !isLoading
                                    ? { backgroundColor: '#4b5563', color: '#fff' }
                                    : { backgroundColor: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed' }
                            }
                        >
                            {isLoading
                                ? (t('words.saving') || 'Sauvegarde...')
                                : (t('words.save'))}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
